import { useState } from 'react'
import type { Cuadro, Traza } from '@domain/rules/cuentas.ts'
import { normalizar } from '@domain/rules/mates.ts'
import { CuadroView } from './Cuadro.tsx'
import { Teclado } from './Teclado.tsx'
import { es } from '../strings/es.ts'
import { textoDelPaso } from '../strings/pasos.ts'

/**
 * Una cuenta paso a paso, como en la pizarra.
 *
 * Dos modos con la misma pantalla:
 *
 * **Ejemplo**: todos los pasos hechos, uno por toque. Es lo que se enseña antes
 * de pedir una parecida, y lo que se ofrece tras un fallo. Un ejemplo resuelto
 * enseña más que pelearse con el problema cuando se está empezando; es el
 * hallazgo más repetido de la investigación sobre ejemplos resueltos
 * (Atkinson, Derry, Renkl y Wortham, 2000).
 *
 * **Guiado**: los primeros pasos vienen hechos y los últimos `pasosQueHace`
 * los contesta el niño. Empezar por el final es a propósito —desvanecimiento
 * hacia atrás, Renkl y Atkinson (2003)—: así ve el camino entero antes de
 * tener que andarlo, y cada acierto le deja un paso más.
 *
 * En cada paso que contesta hay dos pistas, de lo general a lo concreto, y se
 * puede pedir la primera sin haber fallado. La respuesta no es una pista: solo
 * se enseña después de dos intentos, y entonces se escribe y se sigue. Es la
 * lección de Koedinger y Aleven (2007): con la respuesta a un botón, los
 * alumnos la pulsan sin leer lo que había antes.
 */

export interface ResultadoPasos {
  /** Respuestas equivocadas en total. */
  readonly fallos: number
  /** Pasos que hubo que decirle: no los supo ni con las dos pistas. */
  readonly dichos: number
}

type Estado = 'pregunta' | 'bien' | 'dicho'

/**
 * El cuadro antes del primer paso: el de después con lo nuevo sin escribir.
 * Los pasos solo guardan cómo queda la cuenta después de cada uno; el momento
 * de antes del primero se saca borrando lo que ese paso escribió.
 */
function antesDelPrimero(cuadro: Cuadro): Cuadro {
  if (cuadro.tipo === 'columna') {
    return {
      ...cuadro,
      filas: cuadro.filas.map((fila) =>
        fila.papel === 'operando'
          ? { ...fila, celdas: fila.celdas.map((c) => ({ t: c.t })) }
          : { ...fila, celdas: fila.celdas.map((c) => (c.nuevo ? { t: '' } : c)) },
      ),
    }
  }
  if (cuadro.tipo === 'caja') {
    return {
      tipo: 'caja',
      dividendo: cuadro.dividendo.filter((c) => !c.tenue).map((c) => ({ t: c.t })),
      divisor: cuadro.divisor.map((c) => ({ t: c.t })),
      cociente: [],
      restos: [],
    }
  }
  return cuadro
}

export function PasoAPaso({
  traza,
  pide,
  modo,
  pasosQueHace,
  color,
  titulo,
  botonFinal,
  onFin,
}: {
  traza: Traza
  pide: string
  modo: 'ejemplo' | 'guiado'
  /** En modo guiado: cuántos de los pasos que preguntan contesta el niño, desde el final. */
  pasosQueHace: number
  color: string
  titulo: string
  botonFinal: string
  onFin: (resultado: ResultadoPasos) => void
}) {
  const [i, setI] = useState(0)
  const [escrito, setEscrito] = useState('')
  const [estado, setEstado] = useState<Estado>('pregunta')
  const [intentos, setIntentos] = useState(0)
  const [pistasVistas, setPistasVistas] = useState(0)
  const [fallos, setFallos] = useState(0)
  const [dichos, setDichos] = useState(0)

  const preguntan = traza.pasos.flatMap((p, k) => (p.respuesta === undefined ? [] : [k]))
  const hace = new Set(modo === 'guiado' ? preguntan.slice(-pasosQueHace) : [])

  const paso = traza.pasos[i]
  if (!paso) return null
  const texto = textoDelPaso(paso)
  const anterior = i > 0 ? traza.pasos[i - 1] : undefined
  const contesta = hace.has(i) && estado === 'pregunta'
  const cuadro = contesta ? (anterior?.cuadro ?? antesDelPrimero(paso.cuadro)) : paso.cuadro
  const ultimo = i === traza.pasos.length - 1

  const comprobar = (): void => {
    if (escrito === '' || paso.respuesta === undefined) return
    if (normalizar(escrito) === normalizar(paso.respuesta)) {
      setEstado('bien')
      return
    }
    const van = intentos + 1
    setFallos(fallos + 1)
    setIntentos(van)
    setEscrito('')
    if (van >= 2) {
      setDichos(dichos + 1)
      setEstado('dicho')
      return
    }
    setPistasVistas(Math.max(pistasVistas, van))
  }

  const seguir = (): void => {
    if (ultimo) {
      onFin({ fallos, dichos })
      return
    }
    setI(i + 1)
    setEscrito('')
    setEstado('pregunta')
    setIntentos(0)
    setPistasVistas(0)
  }

  return (
    <div>
      <div className="mb-4 flex items-baseline justify-between gap-4">
        <p className="m-0 text-lg font-bold">{titulo}</p>
        <p className="text-muted m-0 text-sm font-bold">
          {es.mates.pasoDe(i + 1, traza.pasos.length)}
        </p>
      </div>
      <p
        data-testid="pide"
        className="text-muted m-0 mb-4 text-center text-xl font-bold tabular-nums"
      >
        {pide}
      </p>

      <CuadroView cuadro={cuadro} color={color} />

      {anterior && !contesta ? (
        <p className="text-muted m-0 mt-4 text-sm">{textoDelPaso(anterior).explica}</p>
      ) : null}

      {contesta ? (
        <div role="group" aria-label={es.mates.teToca}>
          <p className="m-0 mt-4 text-lg font-bold" data-testid="pregunta">
            {texto.pregunta}
          </p>
          {intentos > 0 ? <p className="m-0 mt-2 font-bold">{es.mates.pasoNoEsEsa}</p> : null}
          {texto.pistas.slice(0, pistasVistas).map((pista) => (
            <p key={pista} className="bg-gold-soft m-0 mt-2 rounded-lg px-3 py-2">
              {pista}
            </p>
          ))}
          <Teclado escrito={escrito} onEscrito={setEscrito} onComprobar={comprobar} />
          {pistasVistas < texto.pistas.length ? (
            <button
              type="button"
              className="border-rule mt-3 min-h-12 w-full rounded-xl border px-4 font-bold"
              onClick={() => {
                setPistasVistas(pistasVistas + 1)
              }}
            >
              {pistasVistas === 0 ? es.mates.pista : es.mates.otraPista}
            </button>
          ) : null}
        </div>
      ) : (
        <div role="status">
          {estado === 'bien' ? <p className="m-0 mt-4 font-bold">{es.mates.pasoBien}</p> : null}
          {estado === 'dicho' && paso.respuesta !== undefined ? (
            <p className="m-0 mt-4 font-bold">{es.mates.pasoTeLoDigo(paso.respuesta)}</p>
          ) : null}
          <p className="m-0 mt-2 text-lg" data-testid="explica">
            {texto.explica}
          </p>
          <button
            type="button"
            className="bg-teal mt-6 min-h-14 w-full rounded-xl px-6 text-lg font-bold text-white"
            onClick={seguir}
          >
            {ultimo ? botonFinal : es.mates.sigo}
          </button>
        </div>
      )}
    </div>
  )
}
